import React, { useEffect, useState } from 'react';
import { Button, InputPassword, Modal, SparkIcon, SparkModalPortal, TooltipTrigger } from '@spark';
import { handleCopy } from '@/shared/utils/helpers';
import './settings.css';

/**
 * Reusable editable input for secrets/keys (Spark), matching the Figma
 * masked-key row: 360px disabled-styled input with a lock prefix and the
 * masked value, plus copy / edit / delete actions.
 * - View mode: masked value, copy (beside the field, or inside it when
 *   `copyInsideInput`), optional edit (`showEdit`), optional delete (with confirm)
 * - Edit mode: InputPassword with save / cancel actions
 */
const EditableSecretInput = ({
    value,
    onSave,
    onDelete,
    placeholder = 'Enter value...',
    copyEnabled = true,
    copyInsideInput = false,
    showEdit = true,
    editable = true,
    maskFn,
    confirmDeleteTitle = 'Delete this item?',
    confirmDeleteDescription,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [localValue, setLocalValue] = useState(value || '');
    const [isSaving, setIsSaving] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState(false);

    useEffect(() => {
        if (!isEditing) {
            setLocalValue(value || '');
        }
    }, [value, isEditing]);

    const masked = typeof maskFn === 'function'
        ? maskFn(value || '')
        : defaultMask(value || '');

    const saveEdit = async () => {
        if (!onSave) {
            setIsEditing(false);
            return;
        }
        try {
            const maybePromise = onSave(localValue);
            if (maybePromise && typeof maybePromise.then === 'function') {
                setIsSaving(true);
                await maybePromise;
            }
            setIsEditing(false);
        } catch (e) {
            // Keep the editor open so the user can fix the value.
        } finally {
            setIsSaving(false);
        }
    };

    const maskedField = (
        <div className="spark-input spark-input-large spark-input-disabled settings-secret__masked">
            <div className="spark-input-field-wrap">
                <div className="spark-input-anatomy">
                    <SparkIcon slug="lock-password-icon" size="small" className="spark-input-prefix-icon" />
                    <span className="settings-secret__masked-value">{masked}</span>
                    {copyEnabled && copyInsideInput ? (
                        <TooltipTrigger text="Copy to clipboard">
                            {/* Same in-field action pattern as InputPassword's eye toggle. */}
                            <button
                                type="button"
                                className="spark-input-password-toggle"
                                aria-label="Copy to clipboard"
                                onClick={() => handleCopy(value || '')}
                            >
                                <SparkIcon slug="copy" size="small" />
                            </button>
                        </TooltipTrigger>
                    ) : null}
                </div>
            </div>
        </div>
    );

    if (!editable) {
        return maskedField;
    }

    if (isEditing) {
        return (
            <div className="settings-keys__row">
                <InputPassword
                    size="large"
                    showLabel={false}
                    showPrefixIcon
                    value={localValue}
                    onChange={(e) => setLocalValue(e.target.value)}
                    placeholder={placeholder}
                    className="settings-secret__input"
                />
                <Button
                    size="tiny"
                    variant="ghost"
                    iconSlug="check"
                    aria-label="Save"
                    startIcon={<SparkIcon slug="check" size="small" />}
                    disabled={isSaving}
                    disableWhenReadOnly
                    onClick={saveEdit}
                />
                <Button
                    size="tiny"
                    variant="ghost"
                    iconSlug="close"
                    aria-label="Cancel"
                    startIcon={<SparkIcon slug="close" size="small" />}
                    onClick={() => { setIsEditing(false); setLocalValue(value || ''); }}
                />
            </div>
        );
    }

    return (
        <div className="settings-keys__row">
            {maskedField}
            {copyEnabled && !copyInsideInput ? (
                <Button
                    size="tiny"
                    variant="ghost"
                    iconSlug="copy"
                    aria-label="Copy to clipboard"
                    startIcon={<SparkIcon slug="copy" size="small" />}
                    onClick={() => handleCopy(value || '')}
                />
            ) : null}
            {showEdit ? (
                <Button
                    size="tiny"
                    variant="ghost"
                    iconSlug="edit"
                    aria-label="Edit"
                    startIcon={<SparkIcon slug="edit" size="small" />}
                    disableWhenReadOnly
                    onClick={() => { setIsEditing(true); setLocalValue(value || ''); }}
                />
            ) : null}
            {onDelete ? (
                <Button
                    size="tiny"
                    variant="ghost"
                    iconSlug="trash"
                    className="settings-secret__delete"
                    aria-label="Delete"
                    startIcon={<SparkIcon slug="trash" size="small" />}
                    disableWhenReadOnly
                    onClick={() => setConfirmingDelete(true)}
                />
            ) : null}
            {confirmingDelete ? (
                <SparkModalPortal
                    open
                    onClose={() => setConfirmingDelete(false)}
                    size="text"
                >
                    <Modal
                        title={confirmDeleteTitle}
                        subtitle={confirmDeleteDescription}
                        showSubtitle={Boolean(confirmDeleteDescription)}
                        showScrollBar={false}
                        fixedHeight={false}
                        showOverlay={false}
                        danger
                        primaryLabel="Yes, Delete"
                        tertiaryLabel="Cancel"
                        primaryDisableWhenReadOnly
                        onPrimaryClick={() => { setConfirmingDelete(false); onDelete(); }}
                        onTertiaryClick={() => setConfirmingDelete(false)}
                        onCloseClick={() => setConfirmingDelete(false)}
                    />
                </SparkModalPortal>
            ) : null}
        </div>
    );
};

function defaultMask(text) {
    if (!text) return '';
    if (text.length <= 6) return '*'.repeat(text.length);
    return text.substring(0, 3) + '...' + text.substring(text.length - 4);
}

export default EditableSecretInput;
